import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { AdminListPage } from '../components/AdminListPage'
import { useAdminSectionLibrary } from '../hooks/useAdminSectionLibrary'
import type { SectionLibraryDocument } from '@/types'

export function AdminSectionLibraryPage() {
  const navigate = useNavigate()
  const { sections, loading, error, deleteSection } = useAdminSectionLibrary()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<(SectionLibraryDocument & { id: string }) | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)

  async function handleDelete() {
    if (!deleteTarget) return
    setDeletingId(deleteTarget.id)
    try {
      await deleteSection(deleteTarget.id)
    } finally {
      setDeletingId(null)
      setDeleteTarget(null)
      setDeleteOpen(false)
    }
  }

  return (
    <AdminListPage
      title="Section Library"
      error={error}
      loading={loading}
      skeletonRows={5}
      dialog={
        <>
          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <DialogTrigger className="hidden" />
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Section Template</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-foreground">{deleteTarget?.title}</span>? Forms
                using this template will be unaffected, but it will no longer be available to add.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setDeleteOpen(false); setDeleteTarget(null) }}>Cancel</Button>
                <Button variant="destructive" onClick={() => void handleDelete()} disabled={deletingId !== null}>
                  {deletingId !== null ? 'Deleting…' : 'Delete'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Section ID</TableHead>
            <TableHead>Fields</TableHead>
            <TableHead>Category</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <MultiButtonGroup>
          <TableBody>
            {sections.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  No section templates yet. Save one from the Form Builder.
                </TableCell>
              </TableRow>
            ) : (
              sections.map((section) => (
                <TableRow
                  key={section.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/admin/section-library/${section.id}`)}
                >
                  <TableCell className="font-medium">{section.title}</TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">
                    {section.sectionId}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{section.fields?.length ?? 0}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{section.category ?? '—'}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end">
                      <MultiButton
                        variant="outline"
                        size="sm"
                        items={[
                          {
                            id: 'edit',
                            icon: Pencil,
                            label: 'Edit',
                            onClick: () => navigate(`/admin/section-library/${section.id}`),
                          },
                          {
                            id: 'delete',
                            icon: Trash2,
                            label: 'Delete',
                            onClick: () => { setDeleteTarget(section); setDeleteOpen(true) },
                            disabled: deletingId === section.id,
                            className: 'text-destructive hover:text-destructive',
                          },
                        ]}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </MultiButtonGroup>
      </Table>
    </AdminListPage>
  )
}
